import { formatDate, formatTime, type PaymentMethod } from "@luckymenu/core";
import type { TextStyle } from "../doc";
import type { StoredBlock } from "../document";

/**
 * Formatting for printed documents. Amounts are integer paise, printed as
 * rupees with two decimals and no grouping ("1310.00"), so columns stay
 * narrow on 58 mm paper. Only the total carries the rupee sign, which text
 * mode prints as "Rs.".
 *
 * Printed words are English for now: text mode prints English only, and the
 * labels below are the one place to translate when image mode bills in other
 * languages are wanted.
 */

export function money(paise: number): string {
	if (!Number.isSafeInteger(paise)) throw new RangeError(`Not an integer paise value: ${paise}`);
	const abs = Math.abs(paise);
	const text = `${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
	return paise < 0 ? `-${text}` : text;
}

/** A signed amount for adjustments: "+0.22", "-0.40". */
export function signedMoney(paise: number): string {
	return paise > 0 ? `+${money(paise)}` : money(paise);
}

/**
 * Basis points as a percentage: 500 → "5%", 250 → "2.5%", 1234 → "12.34%",
 * and half a rate such as 125 / 2 → "0.625%". A rate, never money.
 */
export function percent(bp: number): string {
	return `${Number((bp / 100).toFixed(3))}%`;
}

/** "29/09/2026" and "7:35 pm" in the restaurant's time zone. */
export function dateAndTime(iso: string, timeZone: string): { date: string; time: string } {
	const at = new Date(iso);
	return { date: formatDate(at, timeZone), time: formatTime(at, timeZone) };
}

export const PAYMENT_LABELS: Readonly<Record<PaymentMethod, string>> = {
	cash: "Cash",
	upi: "UPI",
	card: "Card",
	other: "Other",
	razorpay: "Online",
};

/** Two columns on one line: a label on the left, a value on the right. */
export function pair(
	left: string,
	right: string,
	style?: TextStyle,
	weights: [number, number] = [1, 1],
): StoredBlock {
	return {
		t: "row",
		cols: [
			{ text: left, weight: weights[0] },
			{ text: right, weight: weights[1], align: "right" },
		],
		...(style ? { style } : {}),
	};
}

/** An amount line of a summary: label right-aligned in the wide column, value right-aligned. */
export function sumLine(label: string, value: string): StoredBlock {
	return {
		t: "row",
		cols: [
			{ text: label, weight: 3, align: "right" },
			{ text: value, weight: 1, align: "right" },
		],
	};
}

/** Lines of free text (header, footer), each centred. Empty lines are kept as spacing. */
export function centredLines(text: string | null | undefined): StoredBlock[] {
	if (!text) return [];
	return text
		.split(/\r?\n/)
		.map((line) => line.trim())
		.map((line): StoredBlock => ({ t: "text", text: line, style: { align: "center" } }));
}

/** The copy label ("DUPLICATE") at the top of a reprint. */
export function copyLabelBlock(label: string | null | undefined): StoredBlock[] {
	return label
		? [{ t: "text", text: `** ${label.toUpperCase()} **`, style: { align: "center", bold: true } }]
		: [];
}
